"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Section, Button, type ButtonBlockVariant, type ButtonBlockColor, type ButtonBlockSize, Switch, Label } from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";
import { Loader2, Mail, ChevronRight, Plus, Download } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

const variantOptions = [
  { label: "Solid", value: "solid" },
  { label: "Outline", value: "outline" },
  { label: "Text", value: "text" },
];

const sizeOptions = [
  { label: "Small", value: "sm" },
  { label: "Default", value: "default" },
  { label: "Large", value: "lg" },
  { label: "Icon", value: "icon" },
];

const colorOptions = [
  { label: "Primary", value: "primary" },
  { label: "Secondary", value: "secondary" },
  { label: "Destructive", value: "destructive" },
];

export default function DocsComponentsButton() {
  const [variant, setVariant] = React.useState<ButtonBlockVariant>("solid");
  const [size, setSize] = React.useState<ButtonBlockSize>("default");
  const [color, setColor] = React.useState<ButtonBlockColor>("primary");
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Button
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            Buttons trigger actions or navigation. Use them sparingly and with clear,
            action-oriented labels.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/button" />
        </SectionCard>
      </Section>

      {/* Interactive Playground */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Playground</CardTitle>
            <CardDescription>Experiment with different button configurations</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Preview */}
          <div className="flex items-center justify-center min-h-40 bg-muted/30 rounded-lg border-2 border-dashed">
            <Button
              variant={variant}
              size={size}
              color={color}
              disabled={isDisabled || isLoading}
            >
              {isLoading && <Loader2 className="animate-spin" />}
              {size === "icon" ? <Plus /> : isLoading ? "Loading..." : "Button"}
            </Button>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <SelectBlock
              label="Variant"
              value={variantOptions.find((o) => o.value === variant) || null}
              onChange={(v) => setVariant((v as typeof variantOptions[0])?.value as ButtonBlockVariant)}
              options={variantOptions}
            />
            <SelectBlock
              label="Size"
              value={sizeOptions.find((o) => o.value === size) || null}
              onChange={(v) => setSize((v as typeof sizeOptions[0])?.value as ButtonBlockSize)}
              options={sizeOptions}
            />
            <SelectBlock
              label="Color"
              value={colorOptions.find((o) => o.value === color) || null}
              onChange={(v) => setColor((v as typeof colorOptions[0])?.value as ButtonBlockColor)}
              options={colorOptions}
            />
            <div className="flex items-center gap-3">
              <Switch
                id="disabled"
                checked={isDisabled}
                onCheckedChange={setIsDisabled}
              />
              <Label htmlFor="disabled">Disabled</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="loading"
                checked={isLoading}
                onCheckedChange={setIsLoading}
              />
              <Label htmlFor="loading">Loading</Label>
            </div>
          </div>
        </div>

        {/* Generated Code */}
        <div className="mt-6 space-y-2">
          <Text className="text-sm font-medium">Code:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <code>
              {`<Button${variant !== "solid" ? ` variant="${variant}"` : ""}${size !== "default" ? ` size="${size}"` : ""}${color !== "primary" ? ` color="${color}"` : ""}${isDisabled ? " disabled" : ""}>`}
              {isLoading ? "{isLoading && <Loader2 />} " : ""}
              {size === "icon" ? "<Plus />" : "Button"}
              {"</Button>"}
            </code>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Variants */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Variants</CardTitle>
            <CardDescription>Different visual styles for various contexts</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex flex-wrap gap-4">
          <div className="space-y-2 text-center">
            <Button variant="solid">Solid</Button>
            <Text className="text-xs text-muted-foreground block">Primary actions</Text>
          </div>
          <div className="space-y-2 text-center">
            <Button variant="outline">Outline</Button>
            <Text className="text-xs text-muted-foreground block">Secondary actions</Text>
          </div>
          <div className="space-y-2 text-center">
            <Button variant="text">Text</Button>
            <Text className="text-xs text-muted-foreground block">Tertiary actions</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Sizes */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Sizes</CardTitle>
            <CardDescription>Different sizes for various contexts</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex flex-wrap items-center gap-4">
          <Button size="sm">Small</Button>
          <Button size="default">Default</Button>
          <Button size="lg">Large</Button>
          <Button size="icon"><Plus /></Button>
        </div>
      </SectionCard>
      </Section>

      {/* Colors */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Colors</CardTitle>
            <CardDescription>Semantic color variants</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex flex-wrap gap-4">
          <Button>Primary</Button>
          <Button color="secondary">Secondary</Button>
          <Button color="destructive">Destructive</Button>
        </div>
      </SectionCard>
      </Section>

      {/* With Icons */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>With Icons</CardTitle>
            <CardDescription>Buttons can include leading or trailing icons</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex flex-wrap gap-4">
          <Button>
            <Mail />
            Send Email
          </Button>
          <Button variant="outline">
            Continue
            <ChevronRight />
          </Button>
          <Button variant="text" size="icon">
            <Download />
          </Button>
        </div>
      </SectionCard>
      </Section>

      {/* States */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>States</CardTitle>
            <CardDescription>Disabled and loading states</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex flex-wrap gap-4">
          <Button disabled>Disabled</Button>
          <Button disabled>
            <Loader2 className="animate-spin" />
            Loading...
          </Button>
        </div>
      </SectionCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Default</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">variant</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">"solid" | "outline" | "text"</td>
              <td className="py-2 font-mono text-xs">"solid"</td>
              <td className="py-2 text-muted-foreground">Visual style variant</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">size</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">"default" | "sm" | "lg" | "icon"</td>
              <td className="py-2 font-mono text-xs">"default"</td>
              <td className="py-2 text-muted-foreground">Size of the button</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">color</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">"primary" | "secondary" | "destructive"</td>
              <td className="py-2 font-mono text-xs">"primary"</td>
              <td className="py-2 text-muted-foreground">Color variant</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">disabled</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Disable the button</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">asChild</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
              <td className="py-2 font-mono text-xs">false</td>
              <td className="py-2 text-muted-foreground">Render as child element</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
        header={<CardTitle>Accessibility</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <ul className="space-y-2 text-sm">
          <li>• <strong>Keyboard:</strong> Focus with Tab, activate with Enter or Space</li>
          <li>• <strong>Screen reader:</strong> Uses native button element for proper announcement</li>
          <li>• <strong>Icon buttons:</strong> Always include aria-label for icon-only buttons</li>
          <li>• <strong>Loading state:</strong> Include loading text for screen readers</li>
          <li>• <strong>Disabled:</strong> Still focusable for accessibility, but not activatable</li>
        </ul>
      </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard
          header={<CardTitle className="text-success">✓ Do</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Use primary for main actions</li>
            <li>• Use destructive for delete/remove actions</li>
            <li>• Keep labels short (1-3 words)</li>
            <li>• Use action verbs (Save, Submit, Create)</li>
            <li>• Show loading state during async actions</li>
          </ul>
        </SectionCard>

        <SectionCard
          header={<CardTitle className="text-destructive">✗ Don't</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Don't use multiple primary buttons</li>
            <li>• Don't disable without explanation</li>
            <li>• Don't use vague labels like "Click here"</li>
            <li>• Don't use red for non-destructive actions</li>
            <li>• Don't hide important actions in menus</li>
          </ul>
        </SectionCard>
        </div>
      </Section>
    </>
  );
}
